import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, SignOut, ShieldCheck, List as ListIcon, Gear } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { NAV_BY_ROLE, WHATS_NEW } from "@/lib/nav";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

function BrandMark({ compact }) {
  return (
    <div className="flex items-center gap-2">
      <ShieldCheck size={26} weight="fill" className="text-gold" />
      <div>
        <div className="font-head font-bold text-sm leading-none text-white">grantSME</div>
        {!compact && <div className="text-[10px] text-white/60 mt-1 tracking-wide">GRANT SUBJECT MATTER EXPERT</div>}
      </div>
    </div>
  );
}

function NavList({ nav, isActive, onNavigate, showSettings }) {
  return (
    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
      {nav.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.to);
        return (
          <button
            key={item.to}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => onNavigate(item.to)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
              active ? "bg-white/15 text-white font-medium" : "text-white/75 hover:bg-white/10 hover:text-white"
            }`}
          >
            <Icon size={18} weight={active ? "fill" : "regular"} />
            {item.label}
          </button>
        );
      })}
      {showSettings && (
        <button
          data-testid="nav-settings"
          onClick={() => onNavigate("/settings")}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
            isActive("/settings") ? "bg-white/15 text-white font-medium" : "text-white/75 hover:bg-white/10 hover:text-white"
          }`}
        >
          <Gear size={18} weight={isActive("/settings") ? "fill" : "regular"} />
          Settings
        </button>
      )}
    </nav>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [mobileOpen, setMobileOpen] = useState(false);

  const nav = [...(NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.leadership), WHATS_NEW];
  const showSettings = user?.role === "admin";

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch {}
  };
  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    loadNotifs();
  };

  const openNotif = async (n) => {
    if (!n.read) {
      setNotifs((p) => ({ ...p, unread: Math.max(0, p.unread - 1), items: p.items.map((x) => x.id === n.id ? { ...x, read: true } : x) }));
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
    }
    const base = { contract: "/contracts/", document: "/contracts/", grant: "/grants/" }[n.related_type];
    if (base && n.related_id) navigate(`${base}${n.related_id}`);
  };

  const isActive = (to) => location.pathname === to || (to !== "/dashboard" && location.pathname.startsWith(to));
  const go = (to) => { navigate(to); setMobileOpen(false); };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Desktop sidebar */}
      <aside className="w-64 shrink-0 bg-primary text-primary-foreground hidden lg:flex flex-col sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-white/10"><BrandMark /></div>
        <NavList nav={nav} isActive={isActive} onNavigate={go} showSettings={showSettings} />
        <div className="px-4 py-4 border-t border-white/10 text-[11px] text-white/50">
          {user?.org_name || "grantSME"}
          <div className="mt-0.5">grantSME Workspace</div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-card border-b border-border flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="lg:hidden p-2 -ml-1 rounded-md hover:bg-secondary transition-colors">
                  <ListIcon size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-primary border-0 flex flex-col">
                <div className="px-6 py-5 border-b border-white/10"><BrandMark /></div>
                <NavList nav={nav} isActive={isActive} onNavigate={go} showSettings={showSettings} />
                <div className="px-4 py-4 border-t border-white/10 text-[11px] text-white/50">{user?.org_name || "grantSME"}</div>
              </SheetContent>
            </Sheet>
            <div className="min-w-0">
              <div className="overline text-muted-foreground truncate">{user?.role_label}</div>
              <div className="font-head font-semibold text-sm text-foreground">Workflow Console</div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="notifications-bell" className="relative p-2 rounded-md hover:bg-secondary transition-colors">
                  <Bell size={20} className="text-foreground" />
                  {notifs.unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-alert text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                      {notifs.unread}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[90vw] max-w-96 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <span className="font-head font-semibold text-sm">Notifications</span>
                  <button data-testid="mark-all-read" onClick={markAll} className="text-xs text-primary hover:underline">Mark all read</button>
                </div>
                <ScrollArea className="max-h-80">
                  {notifs.items.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications</div>}
                  {notifs.items.map((n) => (
                    <button key={n.id} data-testid={`notif-${n.id}`} onClick={() => openNotif(n)} className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary transition-colors ${!n.read ? "bg-secondary/60" : ""} ${n.related_id ? "cursor-pointer" : ""}`}>
                      <div className="text-sm font-medium text-foreground">{n.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                      <div className="text-[10px] text-muted-foreground/70 mt-1">{n.created_at?.slice(0, 16).replace("T", " ")}</div>
                    </button>
                  ))}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            <div className="flex items-center gap-2 pl-2 sm:pl-3 sm:border-l border-border">
              <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">
                {user?.name?.[0] || "U"}
              </div>
              <div className="hidden md:block">
                <div className="text-sm font-medium leading-none">{user?.name}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{user?.email}</div>
              </div>
              <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={logout} title="Sign out">
                <SignOut size={18} />
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
          <AnnouncementBanner role={user?.role} />
          {children}
        </main>
      </div>
    </div>
  );
}
